import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { extractErrorMessage } from "../../api/client";
import { usePolling } from "../../hooks/usePolling";
import {
  cancelIndexing,
  deleteSearchEnv,
  formatTtl,
  getSearchEnv,
  patchIndexingDeadline,
  patchSearchEnv,
  reindexSearchEnv,
  SearchEnv,
  SEARCH_ENV_STATUS_COLOR,
  SearchEnvStatus,
  TERMINAL_SEARCH_ENV_STATUSES,
  ttlChipSeverity,
  ttlRemainingMs,
} from "../../api/searchEnvs";
import { getMe, MeResponse } from "../../api/me";
import { CollapsibleSection } from "../CollapsibleSection";
import { CostFormulaPre } from "./CostFormulaPre";
import { SearchEnvActivityPanel } from "./SearchEnvActivityPanel";
import { SearchEnvHandoffPanel } from "./SearchEnvHandoffPanel";

// Shared "live env card" — polls the env detail every 5s while non-
// terminal, renders status / TTL / endpoint / credentials reveal /
// extend / delete / cost. Used by both the workflow-detail panel and
// the standalone /search-envs/<id> page so the two surfaces stay in
// lockstep.
//
// onChanged fires after a successful Extend or Delete so the parent
// can re-fetch its own listing if it needs to (the panel does; the
// detail page just stays here and lets the poll catch up).

interface Props {
  envId: string;
  onChanged?: () => void;
  // When true (the standalone /search-envs/<id> page), the card body
  // is rendered as three CollapsibleSection groups (Status / Access /
  // Lifecycle & Cost) for navigability. When false (embedded inside
  // ExportDetail's SearchEnvPanel, which is already inside a
  // CollapsibleSection), the card stays flat to avoid double-nested
  // chrome.
  sectioned?: boolean;
}

// Render an "Extend by" preset value in the most natural unit:
// hours for sub-day, "Nd" for whole days. Used by both the
// per-env extend row and the consolidated retention controls.
function _extendLabel(hours: number): string {
  if (hours < 24) return `${hours}h`;
  const days = hours / 24;
  return Number.isInteger(days) ? `${days}d` : `${days.toFixed(1)}d`;
}

function fmtUsd(n: number | null | undefined): string {
  if (n === undefined || n === null) return "—";
  return `$${n.toFixed(2)}`;
}

export function SearchEnvCard({ envId, onChanged, sectioned }: Props) {
  const [env, setEnv] = useState<SearchEnv | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revealCreds, setRevealCreds] = useState(false);
  const [extending, setExtending] = useState(false);
  const [extendingDeadline, setExtendingDeadline] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [me, setMe] = useState<MeResponse | null>(null);

  // Cost is admin-facing; non-admins only see it once the env's estimated
  // cost clears $200. Fetch the viewer's role once.
  useEffect(() => {
    getMe()
      .then(setMe)
      .catch(() => undefined);
  }, []);

  // Tick every 30s so the TTL countdown stays accurate even when the
  // underlying env state hasn't changed.
  const [, setTtlTick] = useState(0);
  useEffect(() => {
    const h = setInterval(() => setTtlTick((n) => n + 1), 30_000);
    return () => clearInterval(h);
  }, []);

  usePolling({
    fetcher: () => getSearchEnv(envId),
    onData: setEnv,
    shouldStop: (e) =>
      TERMINAL_SEARCH_ENV_STATUSES.has(e.status as SearchEnvStatus),
    onError: (err) => setError(extractErrorMessage(err)),
    intervalMs: 5000,
    deps: [envId],
  });

  const handleExtend = useCallback(
    async (hours: number) => {
      setExtending(true);
      try {
        await patchSearchEnv(envId, { ttl_hours: hours });
        const fresh = await getSearchEnv(envId);
        setEnv(fresh);
        onChanged?.();
      } catch (err) {
        setError(extractErrorMessage(err));
      } finally {
        setExtending(false);
      }
    },
    [envId, onChanged],
  );

  // Extend the indexing-job's k8s activeDeadlineSeconds. Adds 2h to
  // the current indexing_timeout_hours, clamped to 48h server-side.
  const handleExtendDeadline = useCallback(async () => {
    if (!env) return;
    const current = env.indexing_timeout_hours ?? 6;
    const target = Math.min(48, current + 2);
    setExtendingDeadline(true);
    try {
      await patchIndexingDeadline(envId, target);
      const fresh = await getSearchEnv(envId);
      setEnv(fresh);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setExtendingDeadline(false);
    }
  }, [env, envId]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm("Tear down this search env now? This is immediate.")) {
      return;
    }
    setDeleting(true);
    try {
      await deleteSearchEnv(envId);
      onChanged?.();
      // Re-fetch so the card shows the new ``deleting`` status
      // immediately rather than waiting for the next poll tick.
      const fresh = await getSearchEnv(envId);
      setEnv(fresh);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  }, [envId, onChanged]);

  const handleCancelIndexing = useCallback(async () => {
    if (
      !window.confirm(
        "Cancel the in-flight indexing job? The domain stays up and the " +
          "credentials remain valid. You can trigger 'Re-index' afterwards " +
          "to retry the bulk push.",
      )
    ) {
      return;
    }
    setCancelling(true);
    try {
      await cancelIndexing(envId);
      const fresh = await getSearchEnv(envId);
      setEnv(fresh);
      onChanged?.();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setCancelling(false);
    }
  }, [envId, onChanged]);

  const handleReindex = useCallback(async () => {
    if (
      !window.confirm(
        "Drop the OpenSearch index and re-run the bulk indexer? The " +
          "domain, credentials, and security bindings are preserved.",
      )
    ) {
      return;
    }
    setReindexing(true);
    try {
      await reindexSearchEnv(envId);
      const fresh = await getSearchEnv(envId);
      setEnv(fresh);
      onChanged?.();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setReindexing(false);
    }
  }, [envId, onChanged]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!env) return <CircularProgress size={18} />;

  const ttlMs = ttlRemainingMs(env);
  const sev = ttlChipSeverity(ttlMs);
  const ttlColor =
    sev === "red" ? "error" : sev === "amber" ? "warning" : "default";
  const terminal = TERMINAL_SEARCH_ENV_STATUSES.has(
    env.status as SearchEnvStatus,
  );

  // Each visual block is declared once and then composed either into
  // a flat Stack (embedded usage) or into CollapsibleSection groups
  // (standalone search-env detail page). Keeping the block JSX in
  // local consts means the two layouts share the same content.

  const statusBlock = (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      flexWrap="wrap"
      rowGap={1}
    >
      <Chip
        label={env.status}
        color={SEARCH_ENV_STATUS_COLOR[env.status as SearchEnvStatus]}
        size="small"
      />
      {!terminal && (
        <Chip
          label={`expires in ${formatTtl(ttlMs)}`}
          color={ttlColor}
          size="small"
          variant={sev === "ok" ? "outlined" : "filled"}
        />
      )}
      <Typography variant="body2" color="text.secondary">
        {env.instance_class} × {env.instance_count} ·{" "}
        {env.data_size_gib?.toFixed(1)} GiB source
      </Typography>
      {env.doc_count !== null && env.doc_count !== undefined && (
        <Typography variant="body2" color="text.secondary">
          {env.doc_count.toLocaleString()} docs indexed
        </Typography>
      )}
    </Stack>
  );

  const domainStatus = <DomainStatusLine env={env} />;
  const domainHealthAlert = <DomainHealthAlert env={env} />;
  const indexingProgress = (
    <IndexingProgress
      env={env}
      onExtend={handleExtendDeadline}
      extending={extendingDeadline}
      onCancel={handleCancelIndexing}
      cancelling={cancelling}
    />
  );
  const errorAlert = env.error_message ? (
    <Alert severity="error" sx={{ mt: 1 }}>
      {env.error_message}
    </Alert>
  ) : null;

  const accessButtons =
    env.status === "ready" && env.dashboards_url ? (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Button
          variant="outlined"
          size="small"
          startIcon={<OpenInNewIcon />}
          component="a"
          href={env.dashboards_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open Dashboards
        </Button>
        <Button
          size="small"
          startIcon={revealCreds ? <VisibilityOffIcon /> : <VisibilityIcon />}
          onClick={() => setRevealCreds((v) => !v)}
        >
          {revealCreds ? "Hide" : "Show"} credentials
        </Button>
      </Stack>
    ) : null;

  // MCP endpoint surface — only when provisioning succeeded. The
  // URL is what the customer points an MCP-capable client (Claude
  // Desktop, Cursor, agents) at; the same Basic-auth credentials
  // gate it as OpenSearch, so we don't need a second creds block.
  const mcpBlock =
    env.status === "ready" && env.mcp_endpoint_url ? (
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1,
          backgroundColor: "background.default",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
        >
          MCP endpoint
        </Typography>
        <CredentialLine label="URL" value={env.mcp_endpoint_url} mono />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5 }}
        >
          Streamable HTTP. Use the customer credentials shown below to
          connect from an MCP-capable client.
        </Typography>
      </Box>
    ) : null;

  const credentialsBlock = revealCreds ? (
    <Stack spacing={1.5}>
      {/* Customer creds first — these are what you actually hand to
          the end user. Read-only on paim-*, edit Dashboards within
          the global tenant, no cluster / security access. */}
      {env.customer_user && env.customer_password && (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundColor: "background.default",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
          >
            Customer (limited)
          </Typography>
          <CredentialLine label="User" value={env.customer_user} />
          <CredentialLine
            label="Password"
            value={env.customer_password}
            mono
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            Read + Dashboards edit. No security or cluster management.
          </Typography>
        </Box>
      )}
      {/* Admin creds — operator-internal account used for setup
          (index template, dashboards seed, user provisioning).
          Surfaced for debugging; the customer should use the limited
          account above. */}
      {env.master_user && env.master_password && (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundColor: "background.default",
            border: "1px solid",
            borderColor: "divider",
            opacity: 0.85,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
          >
            Admin (full access — debug only)
          </Typography>
          <CredentialLine label="User" value={env.master_user} />
          <CredentialLine
            label="Password"
            value={env.master_password}
            mono
          />
        </Box>
      )}
    </Stack>
  ) : null;

  // Per-status action set. ``Extend`` makes sense only for live envs;
  // ``Reindex`` is the recovery path for both healthy (``ready``)
  // envs that want fresh data and ``failed`` envs that want to retry
  // after a cancel; ``Delete`` works everywhere except already-gone
  // states.
  const canExtend = !terminal;
  const canReindex = env.status === "ready" || env.status === "failed";
  const canDelete = !["deleted", "deleting"].includes(env.status);
  const lifecycleActions =
    canExtend || canReindex || canDelete ? (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        {canExtend && (
          <>
            <Typography variant="body2" color="text.secondary">
              Extend by:
            </Typography>
            {[1, 4, 24, 24 * 7, 24 * 30, 24 * 90].map((h) => (
              <Button
                key={h}
                size="small"
                variant="text"
                disabled={extending}
                onClick={() => handleExtend(h)}
              >
                {_extendLabel(h)}
              </Button>
            ))}
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {canReindex && (
          <Button
            size="small"
            variant="outlined"
            disabled={reindexing}
            onClick={handleReindex}
          >
            {reindexing ? "Re-indexing…" : "Re-index"}
          </Button>
        )}
        {canDelete && (
          <Button
            color="error"
            size="small"
            disabled={deleting}
            onClick={handleDelete}
          >
            Delete now
          </Button>
        )}
      </Stack>
    ) : null;

  // Handoff panel only renders when the env is ready AND has customer
  // credentials (post-v7 schema). Hidden during provisioning +
  // indexing because the dashboards URL / final doc count aren't
  // populated yet.
  const handoffPanel = (
    <SearchEnvHandoffPanel
      envId={env.id}
      enabled={
        env.status === "ready" &&
        !!env.customer_user &&
        !!env.customer_password
      }
    />
  );
  // Non-admins only see cost when the env's projected cost exceeds $200
  // (null/unknown → hidden); admins always see it.
  const isAdmin = me?.role === "admin";
  const showCost = isAdmin || (env.projected_usd ?? 0) > 200;
  const costPanel = showCost ? <CostPanel env={env} /> : null;
  const activityPanel = (
    <SearchEnvActivityPanel
      envId={env.id}
      defaultExpanded={env.status === "failed"}
    />
  );

  if (sectioned) {
    // Default-open for Status (operational state the user always wants
    // to see). Customer Access defaults open when the env is ready
    // (the user is there to use the data) and closed otherwise (no
    // creds / dashboards to surface yet). Lifecycle & Cost defaults
    // closed — secondary controls and bookkeeping.
    return (
      <Stack spacing={0}>
        <CollapsibleSection title="Status">
          <Stack spacing={2}>
            {statusBlock}
            {domainStatus}
            {domainHealthAlert}
            {indexingProgress}
            {errorAlert}
          </Stack>
        </CollapsibleSection>
        <CollapsibleSection
          title="Customer Access"
          defaultOpen={env.status === "ready"}
        >
          <Stack spacing={2}>
            {accessButtons || (
              <Typography variant="body2" color="text.secondary">
                Dashboards URL and customer credentials appear here once
                the env reaches <code>ready</code>.
              </Typography>
            )}
            {mcpBlock}
            {credentialsBlock}
            {handoffPanel}
          </Stack>
        </CollapsibleSection>
        <CollapsibleSection
          title={showCost ? "Lifecycle & Cost" : "Lifecycle"}
          defaultOpen={false}
        >
          <Stack spacing={2}>
            {lifecycleActions}
            {costPanel}
            {activityPanel}
          </Stack>
        </CollapsibleSection>
      </Stack>
    );
  }

  // Flat (embedded) render — used inside ExportDetail. We deliberately
  // omit the per-env CostPanel here because the parent page already
  // shows the aggregate Cost Summary; rendering both is repetitive.
  // The standalone /search-envs/<id> page passes ``sectioned`` and
  // gets the full UI including the cost block.
  return (
    <Stack spacing={2}>
      {statusBlock}
      {domainStatus}
      {indexingProgress}
      {accessButtons}
      {credentialsBlock}
      {lifecycleActions}
      {errorAlert}
      {handoffPanel}
      {activityPanel}
    </Stack>
  );
}

function DomainStatusLine({ env }: { env: SearchEnv }) {
  const ds = env.domain_status;
  if (!ds || env.status === "deleted") return null;
  if (ds.error) {
    return (
      <Typography variant="caption" color="text.secondary">
        Domain status: <em>error</em> ({ds.error})
      </Typography>
    );
  }
  const label = !ds.exists
    ? "gone"
    : ds.processing
      ? `${ds.raw_status ?? "processing"} (in flight)`
      : (ds.raw_status ?? "active");
  const colour: "default" | "success" | "warning" =
    !ds.exists ? "default" : ds.processing ? "warning" : "success";
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption" color="text.secondary">
        Domain:
      </Typography>
      <Chip
        label={label}
        size="small"
        color={colour}
        variant="outlined"
        sx={{ height: 20, fontSize: 11 }}
      />
      {env.endpoint_url && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: "monospace" }}
        >
          {env.endpoint_url.replace(/^https?:\/\//, "")}
        </Typography>
      )}
    </Stack>
  );
}

// Surface AWS DescribeDomainHealth warnings — only renders when
// something is genuinely off so the card stays quiet for healthy
// envs. Mirrors the AWS console's "Cluster health: yellow",
// "Master node unavailable", "Unassigned shards" banners.
function DomainHealthAlert({ env }: { env: SearchEnv }) {
  const dh = env.domain_health;
  if (!dh || env.status === "deleted") return null;
  if (!dh.exists) return null;
  if (dh.error) {
    // Soft-fail: a transient DescribeDomainHealth blip shouldn't
    // shout in the UI. Render a low-key caption instead of an Alert.
    return (
      <Typography variant="caption" color="text.secondary">
        Cluster health: <em>error</em> ({dh.error})
      </Typography>
    );
  }
  const issues: string[] = [];
  if (dh.cluster_health && dh.cluster_health !== "Green") {
    issues.push(`Cluster health is ${dh.cluster_health}.`);
  }
  if (dh.master_node && dh.master_node !== "Available") {
    issues.push(`Master node ${dh.master_node.toLowerCase()}.`);
  }
  // unassigned_shards is a string ("0" / "1" / "NotAvailable") —
  // only warn when it parses to a positive integer.
  const unassigned = dh.unassigned_shards ? parseInt(dh.unassigned_shards, 10) : 0;
  if (Number.isFinite(unassigned) && unassigned > 0) {
    issues.push(`${unassigned} unassigned shard${unassigned === 1 ? "" : "s"}.`);
  }
  // AZ pressure: any zone in a non-Active state, or fewer active
  // AZs than configured.
  const azIssues = (dh.az_statuses ?? []).filter((z) => z.status !== "Active");
  for (const z of azIssues) {
    issues.push(`AZ ${z.zone}: ${z.status}.`);
  }
  const azCount = dh.az_count ? parseInt(dh.az_count, 10) : null;
  const activeAz = dh.active_az_count ? parseInt(dh.active_az_count, 10) : null;
  if (
    azCount !== null &&
    activeAz !== null &&
    Number.isFinite(azCount) &&
    Number.isFinite(activeAz) &&
    activeAz < azCount
  ) {
    issues.push(`${activeAz} of ${azCount} availability zones active.`);
  }

  if (issues.length === 0) return null;
  // Red cluster health or master-unavailable is severity=error; the
  // softer signals (unassigned shards, AZ drift) are warning.
  const severity: "error" | "warning" =
    dh.cluster_health === "Red" ||
    (dh.master_node && dh.master_node !== "Available")
      ? "error"
      : "warning";
  return (
    <Alert severity={severity} sx={{ py: 0.5 }}>
      <Stack spacing={0.25}>
        {issues.map((line, i) => (
          <Typography key={i} variant="body2">
            {line}
          </Typography>
        ))}
      </Stack>
    </Alert>
  );
}

function IndexingProgress({
  env,
  onExtend,
  extending,
  onCancel,
  cancelling,
}: {
  env: SearchEnv;
  onExtend: () => void;
  extending: boolean;
  onCancel: () => void;
  cancelling: boolean;
}) {
  if (env.status !== "indexing") return null;
  const p = env.indexing_progress;
  const completed = p?.completed ?? null;
  const total = p?.total ?? null;
  const pct =
    completed !== null && total !== null && total > 0
      ? Math.min(100, Math.round((completed / total) * 100))
      : null;

  // Pull docs_indexed / docs_per_sec out of the worker's counters
  // snapshot. The worker writes both on each report tick (every 5s
  // wall-clock during the bulk push).
  let docsIndexed: number | null = null;
  let docsPerSec: number | null = null;
  if (p?.counters_json) {
    try {
      const c = JSON.parse(p.counters_json);
      if (typeof c.docs_indexed === "number") docsIndexed = c.docs_indexed;
      if (typeof c.docs_per_sec === "number") docsPerSec = c.docs_per_sec;
    } catch {
      // ignore — fall back to progress message
    }
  }

  // Worker pod identity. ``k8s_job_name`` is the Job name; the actual
  // pod adds a random suffix kubelet appends. ``image`` carries the
  // full registry path + tag — slice off everything up to the last
  // colon so we just show the tag in the UI.
  const jobName = p?.k8s_job_name ?? null;
  const imageTag = p?.image ? p.image.split(":").pop() ?? p.image : null;

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        backgroundColor: "background.default",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mb: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="caption" color="text.secondary">
            Bulk indexing
          </Typography>
          {env.indexing_timeout_hours && (
            <Typography variant="caption" color="text.secondary">
              · deadline {env.indexing_timeout_hours}h
            </Typography>
          )}
          <Button
            size="small"
            variant="text"
            onClick={onExtend}
            disabled={extending || (env.indexing_timeout_hours ?? 6) >= 48}
            sx={{ minWidth: 0, py: 0, fontSize: 11 }}
          >
            +2h
          </Button>
          <Button
            size="small"
            variant="text"
            color="error"
            onClick={onCancel}
            disabled={cancelling}
            sx={{ minWidth: 0, py: 0, fontSize: 11 }}
          >
            cancel
          </Button>
        </Stack>
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {docsIndexed !== null ? `${docsIndexed.toLocaleString()} docs` : "—"}
          {docsPerSec !== null ? ` · ${Math.round(docsPerSec)} docs/s` : ""}
          {pct !== null ? ` · ${pct}%` : ""}
        </Typography>
      </Stack>
      <LinearProgress
        variant={pct !== null ? "determinate" : "indeterminate"}
        value={pct ?? undefined}
      />
      {(jobName || imageTag) && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            mt: 0.5,
            display: "block",
            fontFamily: "monospace",
            fontSize: 11,
          }}
        >
          {jobName ?? "—"}
          {imageTag ? ` · ${imageTag}` : ""}
        </Typography>
      )}
    </Box>
  );
}

function CostPanel({ env }: { env: SearchEnv }) {
  const [expanded, setExpanded] = useState(false);
  if (env.incurred_usd === undefined || env.projected_usd === undefined) {
    return null;
  }
  const b = env.breakdown;
  const explanation = env.cost_explanation;
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        backgroundColor: "background.default",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Cost Estimate
          </Typography>
          {explanation && (
            <IconButton
              size="small"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? "Hide cost breakdown" : "Show cost breakdown"}
              sx={{ p: 0.25 }}
            >
              {expanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </Stack>
        <Typography variant="body2">
          <strong>{fmtUsd(env.incurred_usd)}</strong> so far ·{" "}
          {fmtUsd(env.projected_usd)} projected at expiry
        </Typography>
      </Stack>
      {b && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 0.5 }}
        >
          compute {fmtUsd(b.compute_usd)} · storage {fmtUsd(b.storage_usd)} ·
          egress {fmtUsd(b.egress_usd)}
        </Typography>
      )}
      {expanded && explanation && (
        <CostFormulaPre formulaLines={explanation.formula_lines} />
      )}
    </Box>
  );
}

function CredentialLine({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 70 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontFamily: mono ? "monospace" : undefined, flexGrow: 1 }}
      >
        {value}
      </Typography>
      <Tooltip title={copied ? "Copied" : "Copy"}>
        <IconButton
          size="small"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch {
              // Clipboard API requires secure context; silently no-op
              // in dev over plain http. User can still select+copy.
            }
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
